import type { ReactNode } from 'react';
import { Alert, Chip, Stack, Text } from '@billda/design-system';
import {
  useAllPermissions,
  useAnyPermission,
  usePermission,
} from '@billda/permissions-ui';

interface PermissionGateProps {
  permission?: string;
  anyOf?: string[];
  allOf?: string[];
  children: ReactNode;
  fallback?: ReactNode;
  showRequiredPermissions?: boolean; // default true
}

/**
 * DS fork of the permissions-ui package's PermissionGate — identical gate
 * semantics; the denied message renders DS Alert/Chip instead of MUI.
 */
export const PermissionGate = ({
  permission,
  anyOf,
  allOf,
  children,
  fallback,
  showRequiredPermissions = true,
}: PermissionGateProps) => {
  const singlePermission = usePermission(permission || '');
  const anyPermission = useAnyPermission(anyOf || []);
  const allPermissions = useAllPermissions(allOf || []);

  const has = permission
    ? singlePermission
    : anyOf
      ? anyPermission
      : allOf
        ? allPermissions
        : true;

  if (has) {
    return <>{children}</>;
  }
  if (fallback !== undefined) {
    return <>{fallback}</>;
  }
  if (!showRequiredPermissions) {
    return null;
  }

  const requiredPermissions = permission
    ? [permission]
    : anyOf
      ? anyOf
      : allOf
        ? allOf
        : [];
  const requirementType: 'single' | 'any' | 'all' = permission
    ? 'single'
    : anyOf
      ? 'any'
      : 'all';

  const getMessage = () => {
    if (requiredPermissions.length === 0) {
      return 'Access denied';
    }
    if (requiredPermissions.length === 1) {
      return 'You need the following permission:';
    }
    switch (requirementType) {
      case 'any':
        return 'You need at least one of these permissions:';
      case 'all':
        return 'You need all of these permissions:';
      default:
        return 'You need the following permission:';
    }
  };

  return (
    <Alert severity="info" data-testid="permission-message">
      <Text variant="body2" className="font-semibold">
        Permission Required
      </Text>
      <Text variant="body2" className="mb-2">
        {getMessage()}
      </Text>
      <Stack direction="row" spacing={1} className="flex-wrap">
        {requiredPermissions.map(p => (
          <Chip
            key={p}
            data-testid="permission-chip"
            label={p}
            size="sm"
            variant="outlined"
            className="font-mono text-sm"
          />
        ))}
      </Stack>
    </Alert>
  );
};
